<template>
    <span ref="root" class="animated-highlight" :class="{ 'animated-highlight_nowrap': unbrokenValue }">
        <span class="sr-only">{{ text }}</span>
        <span class="animated-highlight__visual" aria-hidden="true">
            <template v-for="(part, index) in parts" :key="index">
                <span v-if="part.number" class="animated-highlight__number">
                    <span class="animated-highlight__measure">{{ part.text }}</span>
                    <span class="animated-highlight__value">{{ displayedNumber(part) }}</span>
                </span>
                <span v-else>{{ part.text }}</span>
            </template>
        </span>
    </span>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { formatAnimatedValue, splitAnimatedText, type AnimatedTextPart } from './utils.ts';

defineOptions({ name: 'AAnimatedHighlight' });

const props = defineProps({
    text: { type: String, required: true },
    animate: { type: Boolean, default: true },
    duration: { type: Number, default: 1200 },
    firstNumberOnly: { type: Boolean, default: false },
});

const root = ref<HTMLElement | null>(null);
// Null keeps SSR and the initial hydration render at the authored final value.
const progress = ref<number | null>(null);
const parts = computed(() => splitAnimatedText(props.text, props.firstNumberOnly));
const hasNumbers = computed(() => parts.value.some((part) => part.number));
// Numeric grouping spaces stay inside their slot; authored word spaces may wrap.
const unbrokenValue = computed(() => props.firstNumberOnly && parts.value.every((part) => part.number || !/\s/.test(part.text)));

const displayedNumber = (part: AnimatedTextPart) => {
    if (!part.number || progress.value === null || progress.value === 1) return part.text;
    return formatAnimatedValue(part.number, part.number.value * progress.value);
};

let observer: IntersectionObserver | null = null;
let motionQuery: MediaQueryList | null = null;
let animationFrame = 0;
let mounted = false;
let visible = false;
let elapsed = 0;
let previousFrame: number | null = null;

function pauseAnimation() {
    if (animationFrame) cancelAnimationFrame(animationFrame);
    animationFrame = 0;
    previousFrame = null;
}

function cleanup() {
    observer?.disconnect();
    observer = null;
    pauseAnimation();
    visible = false;
    elapsed = 0;
}

function step(now: number) {
    animationFrame = 0;
    if (!visible || document.hidden) {
        pauseAnimation();
        return;
    }

    // Use only animation-frame timestamps: performance.now() can be ahead of
    // the first frame's timestamp, producing a negative starting value.
    if (previousFrame !== null) elapsed += Math.max(0, now - previousFrame);
    previousFrame = now;
    progress.value = Math.min(elapsed / props.duration, 1);

    if (progress.value < 1) {
        animationFrame = requestAnimationFrame(step);
    } else {
        observer?.disconnect();
        observer = null;
    }
}

function updatePlayback() {
    if (!visible || document.hidden) {
        pauseAnimation();
    } else if (!animationFrame && progress.value !== null && progress.value < 1) {
        animationFrame = requestAnimationFrame(step);
    }
}

function prepareAnimation() {
    cleanup();
    progress.value = null;

    if (!mounted || !props.animate || !hasNumbers.value) return;
    if (motionQuery?.matches || !Number.isFinite(props.duration) || props.duration <= 0) return;
    // Without visibility observation, leave the final value readable.
    if (!('IntersectionObserver' in window)) return;

    progress.value = 0;

    observer = new IntersectionObserver((entries) => {
        const entry = entries.at(-1);
        visible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.5);
        updatePlayback();
    }, { threshold: 0.5 });

    if (root.value) observer.observe(root.value);
}

onMounted(() => {
    mounted = true;
    motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    motionQuery.addEventListener('change', prepareAnimation);
    document.addEventListener('visibilitychange', updatePlayback);
    prepareAnimation();
});

watch(() => [props.text, props.animate, props.duration, props.firstNumberOnly], async () => {
    await nextTick();
    prepareAnimation();
});

onBeforeUnmount(() => {
    mounted = false;
    motionQuery?.removeEventListener('change', prepareAnimation);
    document.removeEventListener('visibilitychange', updatePlayback);
    cleanup();
});
</script>

<style lang="pcss" scoped>
.animated-highlight {
    white-space: pre-wrap;

    &_nowrap {
        white-space: nowrap;
    }

    &__visual {
        display: inline;
    }

    &__number {
        display: inline-block;
        position: relative;
        font-variant-numeric: tabular-nums;
        /* Keep wider intermediate digits on one line in their reserved slot. */
        white-space: nowrap;
        overflow-wrap: normal;
    }

    &__measure {
        visibility: hidden;
    }

    &__value {
        position: absolute;
        inset: 0;
        text-align: end;
    }
}
</style>
